/**
 * Ticket-type selection rule (`event.ticketTypeSelection`, nimi-backend #1386 / nimi-app #1556).
 *
 * Mirrors `validateTicketTypeSelection` in the backend `@nimee/calculations` lib so the picker,
 * the dashboard warnings and the link form count exactly what the order route enforces:
 * - only regular tickets count (`type` "ticket" or missing — most prod tickets have no type);
 * - seats events are never limited;
 * - an order with zero regular tickets is never limited (free-ticket links, group captures);
 * - scope "order" (default, also when missing): min/max distinct regular types in the order;
 * - scope "group": max checked separately inside every group the order touches; a
 *   `selectionGroupId` that is not in `groups` counts as ungrouped, and ungrouped tickets are
 *   never limited;
 * - scope "group", per-group limits (#1582, #1591): every group with a minimum (`minTypes`, or
 *   `required: true` = 1) must hold at least that many selected regular types — strict, no
 *   availability carve-out; ungrouped tickets and unknown group ids never count. The max of a
 *   group is its own `maxTypes`, else the rule's. The min pass runs over all groups before the max.
 */

export const SELECTION_SCOPE = { ORDER: 'order', GROUP: 'group' };

export const isRegularTicket = (ticket) => ticket?.type == null || ticket.type === 'ticket';

export const getSelectionRule = (event) => event?.ticketTypeSelection || null;

export const getSelectionScope = (event) =>
    getSelectionRule(event)?.scope === SELECTION_SCOPE.GROUP ? SELECTION_SCOPE.GROUP : SELECTION_SCOPE.ORDER;

/** The rule is on and can apply to this event (seats events bypass it). */
export const isSelectionRuleActive = (event) => Boolean(getSelectionRule(event)?.enabled) && !event?.isSeatsEvent;

export const isGroupScopeActive = (event) =>
    isSelectionRuleActive(event) && getSelectionScope(event) === SELECTION_SCOPE.GROUP;

const toLimit = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) && n >= 1 ? Math.floor(n) : null;
};

export const getMinTypes = (event) => toLimit(getSelectionRule(event)?.minTypes);

export const getMaxTypes = (event) => toLimit(getSelectionRule(event)?.maxTypes);

export const getSelectionGroups = (event) => {
    const groups = getSelectionRule(event)?.groups;
    return Array.isArray(groups) ? groups.filter((g) => g && g.id) : [];
};

/**
 * Minimum distinct regular types to choose from a group, or null (#1591). `minTypes` wins; a group
 * saved before #1591 with only `required: true` counts as 1.
 */
export const getGroupMinTypes = (group) => toLimit(group?.minTypes) ?? (group?.required === true ? 1 : null);

/** Max distinct regular types from a group: its own `maxTypes`, else the rule's, else null (#1591). */
export const getGroupMaxTypes = (event, group) => toLimit(group?.maxTypes) ?? getMaxTypes(event);

/** #1582: a group the buyer must choose from (has a minimum; group scope only, ignored otherwise). */
export const isGroupRequired = (group) => getGroupMinTypes(group) != null;

/** True when every group has the same min and max, so one banner sentence describes them all. */
export const groupsShareOneRange = (event) => {
    const ranges = getSelectionGroups(event).map((g) => `${getGroupMinTypes(g)}|${getGroupMaxTypes(event, g)}`);
    return new Set(ranges).size <= 1;
};

/**
 * Buyer banner (#1591): the one max every group shares, as `{ max }` (max may be null = no max),
 * or null when the groups need their own range lines. Minimum 1 vs none does not split the banner:
 * the "חובה" chip already marks those groups, so events saved before #1591 (rule max + some
 * `required: true` groups) keep today's sentence. A group minimum of 2+ always needs its own line.
 */
export const getSharedGroupBannerMax = (event) => {
    const groups = getSelectionGroups(event);
    if (groups.some((g) => (getGroupMinTypes(g) ?? 0) >= 2)) return null;
    const maxes = new Set(groups.map((g) => getGroupMaxTypes(event, g)));
    if (maxes.size > 1) return null;
    return { max: maxes.size ? [...maxes][0] : getMaxTypes(event) };
};

/** Required groups of the rule, in `groups` order (meaningful only when group scope is active). */
export const getRequiredGroups = (event) => getSelectionGroups(event).filter(isGroupRequired);

/** Group scope is active and at least one group is required. */
export const hasRequiredGroups = (event) => isGroupScopeActive(event) && getRequiredGroups(event).length > 0;

const chosenAmount = (chosenTickets, ticketId) => Number(chosenTickets?.[ticketId]?.amount ?? 0) || 0;

/** Regular tickets with a chosen quantity of at least 1. */
export const getSelectedRegularTickets = (tickets = [], chosenTickets = {}) =>
    tickets.filter((t) => isRegularTicket(t) && chosenAmount(chosenTickets, t._id) >= 1);

export const countSelectedRegular = (tickets = [], chosenTickets = {}) =>
    new Set(getSelectedRegularTickets(tickets, chosenTickets).map((t) => String(t._id))).size;

/** Group id of a ticket, or null when it is ungrouped or points at a group that no longer exists. */
export const resolveTicketGroupId = (event, ticket) => {
    const id = ticket?.selectionGroupId;
    if (!id) return null;
    return getSelectionGroups(event).some((g) => g.id === id) ? id : null;
};

/** Name of the ticket's group, or null (used for the checkout prefix). */
export const resolveTicketGroupName = (event, ticket) => {
    const id = resolveTicketGroupId(event, ticket);
    if (!id) return null;
    return getSelectionGroups(event).find((g) => g.id === id)?.name || null;
};

/** Distinct regular types of `tickets` that belong to `groupId`. */
const countInGroup = (event, tickets, groupId) =>
    new Set(tickets.filter((t) => isRegularTicket(t) && resolveTicketGroupId(event, t) === groupId).map((t) => String(t._id ?? t.name))).size;

/**
 * Groups whose minimum the current selection does not reach, in `groups` order. Empty unless group
 * scope is active (not seats) and >= 1 regular ticket is selected: zero-regular orders (free-ticket
 * links, group captures) are never checked, same as the backend.
 */
export const getMissingRequiredGroups = (event, tickets = [], chosenTickets = {}) => {
    if (!isGroupScopeActive(event)) return [];
    const selected = getSelectedRegularTickets(tickets, chosenTickets);
    if (selected.length === 0) return [];
    return getRequiredGroups(event).filter((g) => countInGroup(event, selected, g.id) < getGroupMinTypes(g));
};

/**
 * Required groups with no purchasable visible regular ticket: the event cannot be ordered from
 * this page (strict rule, #1582 — sold out, disabled, outside the sale window, hidden by the link
 * or badge-blocked all count). `isPurchasable(ticket)` is the picker's own predicate (same pattern
 * as `purchasableRegularCount`); it defaults to "every visible ticket".
 */
export const getUnsatisfiableRequiredGroups = (event, tickets = [], isPurchasable = () => true) => {
    if (!isGroupScopeActive(event)) return [];
    return getRequiredGroups(event).filter((g) =>
        countInGroup(event, tickets.filter((t) => isPurchasable(t)), g.id) < getGroupMinTypes(g));
};

/**
 * Picker "cannot complete" verdict for required groups (#1582), or null. Strict rule:
 * - `{ reason: 'unavailable', group }` — the first required group (in `groups` order) with no
 *   purchasable visible regular types than its minimum (see getUnsatisfiableRequiredGroups);
 * - `{ reason: 'capacity', needed, capacityLeft }` — the order cannot hold every group's minimum
 *   (a ticket is in one group at most, so it needs `needed` = sum of the minimums). Same idea as
 *   the order scope's `capacityLeft < minTypes`. `capacityLeft` is the order's total capacity.
 */
export const getRequiredGroupsBlock = (event, tickets = [], { isPurchasable = () => true, capacityLeft = Infinity } = {}) => {
    if (!hasRequiredGroups(event)) return null;
    const [group] = getUnsatisfiableRequiredGroups(event, tickets, isPurchasable);
    if (group) return { reason: 'unavailable', group };
    const needed = getRequiredGroups(event).reduce((sum, g) => sum + getGroupMinTypes(g), 0);
    if (capacityLeft < needed) return { reason: 'capacity', needed, capacityLeft };
    return null;
};

/**
 * Required groups a link exposes no ticket of: buyers through the link can never complete an
 * order (#1582, links are strict). A link with zero regular tickets (free-ticket-only) is
 * legitimate — orders with no regular ticket are never checked — same as linkExposesTooFewTypes.
 * Pass the live rule as `event` in the dashboard.
 */
export const linkMissingRequiredGroups = (event, linkTickets = []) => {
    if (!isGroupScopeActive(event)) return [];
    const tickets = (linkTickets || []).filter(Boolean);
    if (!tickets.some(isRegularTicket)) return [];
    return getUnsatisfiableRequiredGroups(event, tickets);
};

/**
 * Buckets tickets into sections in `groups` order; ungrouped tickets (and unknown ids) come last
 * with `group: null`. Ticket order inside a section is preserved. Empty sections are dropped.
 * Non-regular tickets (e.g. a SystemFreeTicket on a link) go to the ungrouped section.
 */
export const bucketTicketsByGroup = (event, tickets = []) => {
    const groups = getSelectionGroups(event);
    const byId = new Map(groups.map((g) => [g.id, []]));
    const ungrouped = [];
    tickets.forEach((t) => {
        const id = isRegularTicket(t) ? resolveTicketGroupId(event, t) : null;
        if (id) byId.get(id).push(t);
        else ungrouped.push(t);
    });
    const sections = groups
        .map((g) => ({ group: g, tickets: byId.get(g.id) }))
        .filter((s) => s.tickets.length > 0);
    if (ungrouped.length) sections.push({ group: null, tickets: ungrouped });
    return sections;
};

/**
 * G9: the group's max is 1 and every ticket of it allows at most 1 per order → radio-like picking.
 * Without `group` the rule's max is used.
 */
export const isTapToChooseGroup = (event, groupTickets = [], group = null) =>
    (group ? getGroupMaxTypes(event, group) : getMaxTypes(event)) === 1 &&
    groupTickets.length > 0 &&
    groupTickets.every((t) => Number(t.countTicketsAllowPerPurchase) <= 1);

/**
 * Same verdict as the backend helper, for the current selection.
 * Returns `{ ok: true }` or
 *   `{ ok: false, scope: 'order', selected, minTypes, maxTypes }` /
 *   `{ ok: false, scope: 'group', groupId, groupName, selected, maxTypes }` (group above its max) /
 *   `{ ok: false, scope: 'group', groupId, groupName, kind: 'required', minTypes }` (group below
 *   its minimum; the first one in `groups` order — checked before the max).
 */
export const validateTicketTypeSelection = (event, tickets = [], chosenTickets = {}) => {
    if (!isSelectionRuleActive(event)) return { ok: true };
    const selected = getSelectedRegularTickets(tickets, chosenTickets);
    if (selected.length === 0) return { ok: true };
    const maxTypes = getMaxTypes(event);

    if (getSelectionScope(event) === SELECTION_SCOPE.GROUP) {
        // Minimum pass first: it applies with or without a max.
        const [missing] = getMissingRequiredGroups(event, tickets, chosenTickets);
        if (missing) return { ok: false, scope: SELECTION_SCOPE.GROUP, groupId: missing.id, groupName: missing.name, kind: 'required', minTypes: getGroupMinTypes(missing) };
        for (const group of getSelectionGroups(event)) {
            const groupMax = getGroupMaxTypes(event, group);
            if (groupMax == null) continue;
            const count = countInGroup(event, selected, group.id);
            if (count > groupMax) {
                return { ok: false, scope: SELECTION_SCOPE.GROUP, groupId: group.id, groupName: group.name, selected: count, maxTypes: groupMax };
            }
        }
        return { ok: true };
    }

    const minTypes = getMinTypes(event);
    const count = new Set(selected.map((t) => String(t._id))).size;
    if ((minTypes != null && count < minTypes) || (maxTypes != null && count > maxTypes)) {
        return { ok: false, scope: SELECTION_SCOPE.ORDER, selected: count, minTypes, maxTypes };
    }
    return { ok: true };
};

/**
 * Order-scope state for the public picker (spec §6.2). Pure so the picker and the tests share it.
 *
 * @param {object} event
 * @param {object[]} tickets           tickets visible on the page (after link / sale-window filters)
 * @param {object} chosenTickets       `{ [ticketId]: { amount } }`
 * @param {object} [opts]
 * @param {number} [opts.purchasableRegularCount]  regular types that can still be bought (defaults
 *                                                 to every visible regular type)
 * @param {number} [opts.capacityLeft]  seats left for the whole order (event capacity / slot)
 */
export const getOrderScopePickerState = (event, tickets = [], chosenTickets = {}, opts = {}) => {
    const active = isSelectionRuleActive(event) && getSelectionScope(event) === SELECTION_SCOPE.ORDER;
    const minTypes = getMinTypes(event);
    const maxTypes = getMaxTypes(event);
    const visibleRegular = tickets.filter(isRegularTicket).length;
    const selected = countSelectedRegular(tickets, chosenTickets);
    // Banner only when the rule can mean something here: a free-ticket link that lists only the
    // SystemFreeTicket must not show an unsatisfiable "0 of 3–4" (zero-regular carve-out).
    const showBanner = active && visibleRegular >= 1 && (minTypes != null || maxTypes != null);
    const ruleApplies = active && selected >= 1;
    const belowMin = minTypes != null && selected < minTypes;
    const aboveMax = maxTypes != null && selected > maxTypes;
    const purchasable = opts.purchasableRegularCount ?? visibleRegular;
    const capacityLeft = opts.capacityLeft ?? Infinity;
    const unsatisfiable = showBanner && minTypes != null && (purchasable < minTypes || capacityLeft < minTypes);
    return {
        active,
        showBanner,
        minTypes,
        maxTypes,
        selected,
        inRange: !belowMin && !aboveMax,
        ruleApplies,
        belowMin,
        missing: belowMin ? minTypes - selected : 0,
        // Disable-at-max applies once a regular type is selected.
        atMax: ruleApplies && maxTypes != null && selected >= maxTypes,
        unsatisfiable,
        // Rule-wise permission to continue; the caller still requires hasChosenTickets.
        blocksProceed: ruleApplies && (belowMin || aboveMax || unsatisfiable),
    };
};

/**
 * Per-section state for the group-scope picker (spec §11.7). `section` is one entry of
 * `bucketTicketsByGroup`; the ungrouped section (`group: null`) is never limited.
 */
export const getGroupSectionState = (event, section, chosenTickets = {}) => {
    const group = section?.group || null;
    const maxTypes = group ? getGroupMaxTypes(event, group) : null;
    const minTypes = group && isGroupScopeActive(event) ? getGroupMinTypes(group) : null;
    const limited = maxTypes != null;
    const selected = countSelectedRegular(section?.tickets || [], chosenTickets);
    return {
        limited,
        maxTypes,
        minTypes,
        selected,
        // #1582/#1591: group below its minimum — the "חובה" / "בחרו לפחות N" chip stays until met.
        missingRequired: minTypes != null && selected < minTypes,
        atMax: limited && selected >= maxTypes,
        tapToChoose: limited && isTapToChooseGroup(event, section.tickets, group),
    };
};

/**
 * True when `name` already contains `part` as a standalone token (not "סבב 1" inside "סבב 10"):
 * the characters around the match must not be digits.
 */
const containsToken = (name, part) => {
    if (!part) return false;
    let from = 0;
    while (from <= name.length) {
        const at = name.indexOf(part, from);
        if (at === -1) return false;
        const before = name[at - 1];
        const after = name[at + part.length];
        if (!/\d/.test(before || '') && !/\d/.test(after || '')) return true;
        from = at + 1;
    }
    return false;
};

/**
 * Display label for a ticket in checkout: "{group} · {name}" in group scope (G8), else the name.
 * The prefix is skipped when the ticket name already carries the group — the whole group name, or
 * its first " · " segment (tickets named "סבב 1 · 11:30 | יוגה" in group "סבב 1 · 11:30–12:15").
 */
export const getTicketLabelWithGroup = (event, ticket, fallbackName) => {
    const name = fallbackName ?? ticket?.name ?? '';
    if (!isGroupScopeActive(event) || !ticket) return name;
    const groupName = String(resolveTicketGroupName(event, ticket) || '').trim();
    if (!groupName) return name;
    const firstSegment = groupName.split(' · ')[0].trim();
    if (containsToken(name, groupName) || containsToken(name, firstSegment)) return name;
    return `${groupName} · ${name}`;
};

/**
 * Splits text into plain parts and numeric/time ranges ("11:30–12:15", "3–4") so the caller can
 * wrap ranges in `<bdi dir="ltr">`: inside Hebrew text a bare range renders reversed.
 * @returns {{ text: string, ltr: boolean }[]}
 */
export const splitLtrRanges = (text) => {
    const source = String(text ?? '');
    // Digits, ":", "/", "." and dashes around the match exclude dates and decimals
    // ("01/01/26-31/12/27", "1.5-2", "1/7-5/7", "2025-11-25") — only bare ranges are isolated.
    const re = /(^|[^\d:/.\u2013\u2014-])(\d{1,2}(?::\d{2})?\s?[–—-]\s?\d{1,2}(?::\d{2})?)(?![\d/.\u2013\u2014-]|:\d)/g;
    const parts = [];
    let last = 0;
    let match;
    while ((match = re.exec(source)) !== null) {
        const start = match.index + match[1].length;
        if (start > last) parts.push({ text: source.slice(last, start), ltr: false });
        parts.push({ text: match[2], ltr: true });
        last = start + match[2].length;
    }
    if (last < source.length) parts.push({ text: source.slice(last), ltr: false });
    return parts;
};

/**
 * A link that exposes some regular tickets, but fewer than the order-scope minimum, can never
 * produce a valid order (spec §6.4). A link with zero regular tickets (free-ticket-only) is
 * legitimate: orders with no regular ticket are never limited. Group scope has no minimum.
 * Used by the link form and the selection-limit panel; pass the live rule as `event` there.
 */
export const linkExposesTooFewTypes = (event, linkTickets = []) => {
    if (!isSelectionRuleActive(event) || getSelectionScope(event) !== SELECTION_SCOPE.ORDER) return false;
    const minTypes = getMinTypes(event);
    if (minTypes == null) return false;
    const regular = new Set((linkTickets || []).filter((t) => t && isRegularTicket(t)).map((t) => String(t._id ?? t.name))).size;
    return regular > 0 && regular < minTypes;
};
